// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this
// file, exactly as components/common/MonthlyBreakdownChart.tsx does for the same reason.
import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { CHART_AXIS_TICK, CHART_GRID, TOOLTIP_CARD, gradientDefs } from './trackerKit';

/**
 * Round axis steps, so a share axis reads 0/10/20/30/40 rather than 17.6/25.1/32.6.
 *
 * Recharts will happily place ticks wherever a domain puts them; asking for
 * `dataMin - 0.5` gave three ticks a decimal apart that all rounded to the same
 * printed label, which is what made the panels look like they carried no scale.
 */
const NICE_STEPS = [0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25, 50];

export function niceAxis(values) {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = Math.max(0.5, hi - lo);
  const raw = span / 3;
  const step = NICE_STEPS.find((s) => s >= raw) ?? 100;
  const min = Math.max(0, Math.floor(lo / step) * step);
  // A stage whose share never moves lands on min === max, which is a degenerate
  // domain recharts cannot draw. Give it one step of room so the flat line sits
  // in a panel with a readable scale instead of collapsing.
  const max = Math.ceil(hi / step) * step === min ? min + step : Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = min; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(4)));
  return { min, max, ticks, decimals: step < 1 ? 1 : 0 };
}

/**
 * One stage's share of the book over time.
 *
 * Deliberately NOT a hand-rolled SVG. The first version drew its own paths, which
 * put the Trends tab on a different chart language from every other page and made
 * stroke weight depend on how far the viewBox happened to be scaled: a 2px stroke
 * in a 300-unit box rendered at 420px comes out at 2.8px, so the panels read as
 * heavy beside the Dashboard's. Recharts draws in real pixels, so 2px is 2px here
 * and on the Dashboard.
 *
 * This IS the tracker's reference line style (founder, 5 Oct 2026): monotone curve over the house
 * area gradient (trackerKit chartGradient), the house grid and axis ticks, and the dark tooltip
 * card. Only the density differs, because thirteen of these sit in a grid rather than one filling
 * a page.
 */
const StageTrendChart = ({ label, colour, data }) => {
  const gradientId = `stage-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  const axis = niceAxis(data.map((d) => d.share));

  const CustomTooltip = ({ active, payload, label: pointLabel }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
      <div className={TOOLTIP_CARD}>
        <p className="mb-1 font-bold">{pointLabel}</p>
        <p style={{ color: colour }}>
          {label}: <span className="tabular-nums">{row.share.toFixed(1)}%</span>
        </p>
        <p className="text-xs text-gray-400">
          <span className="tabular-nums">{row.deals.toLocaleString('en-AU')}</span> deals in flight
        </p>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={140}>
      {/* No negative left margin. Pulling the plot left by 22px while the axis kept
          its own width clipped every tick label, so "42.9%" rendered as "4%" and
          three different ticks all printed "1%". The axis gets real width instead. */}
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        {gradientDefs(gradientId, colour)}
        <XAxis dataKey="month" stroke="#344155" tick={{ ...CHART_AXIS_TICK, fontSize: 10 }} tickLine={false} interval="preserveStartEnd" minTickGap={26} />
        {/* Scaled to this stage's own range, on round steps. A shared 0-45% axis
            flattened every stage under 7% into a straight line at the foot of its
            panel; unrounded bounds made the labels unreadable. */}
        <YAxis
          stroke="#344155"
          tick={{ ...CHART_AXIS_TICK, fontSize: 10 }}
          width={46}
          tickLine={false}
          domain={[axis.min, axis.max]}
          ticks={axis.ticks}
          tickFormatter={(v) => `${Number(v).toFixed(axis.decimals)}%`}
        />
        <CartesianGrid {...CHART_GRID} opacity={0.5} />
        <Tooltip cursor={{ stroke: '#344155' }} content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="share"
          stroke={colour}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 5, fill: colour }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default StageTrendChart;
